import {
  useState,
  useEffect,
  useContext,
  Fragment,
  useLayoutEffect,
} from 'react'

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer'

import LoadingContext from '../../context/Loading/Loading'

import { getClubSeatsReport, getSeasons } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import {
  formatShortDate,
  formatCurrency,
  formatNumber,
  formatString,
  capitalizeString,
  timezones,
  setColumnsAndRows,
  setHeaders,
  DEFAULTNUMHEADERS,
  createWorkbook,
  saveWorkbook,
  currencyFormat,
  numberFormat,
  percentFormat,
  addWorksheet,
  styleTotalRow,
  styleSubTotalRow,
  getTimezoneDate,
} from '../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { SeasonSelector } from '../SeasonSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'

import './report-print.scss'

export default function ClubSeatWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const [seasons, setSeasons] = useState()

  const [selectedSeasonId, setSelectedSeasonId] = useState()

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [eventAnalytics, setEventAnalytics] = useState(null)

  useLayoutEffect(() => {
    const root = document.getElementById('root')
    root.classList.add('report-page')

    return () => {
      root.classList.remove('report-page')
    }
  }, [])

  useEffect(() => {
    showLoading()
    loadSeasons(organization.uuid)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedSeasonId) return

      setIsLoadingData(true)

      try {
        const res = await getClubSeatsReport(selectedSeasonId)
        setEventAnalytics(res.data)
      } catch (error) {
        console.error('Error fetching access control summary:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    fetchData()
  }, [selectedSeasonId, organization.uuid, refreshToken])

  const loadSeasons = async (organizationUUID) => {
    let res = await getSeasons(organizationUUID)

    if (res.status === 200) {
      setSeasons(res.data)
      hideLoading()
    }
  }

  // Universal PDF Generator using JSON schema
  const UniversalPDFReport = ({ reportData, organizationName }) => {
    const styles = StyleSheet.create({
      page: { padding: 20, fontSize: 8 },
      headerRow: {
        flexDirection: 'row',
        marginBottom: 15,
        alignItems: 'flex-start',
      },
      logoContainer: {
        width: '25%',
        alignItems: 'flex-start',
        justifyContent: 'center',
      },
      logoText: {
        fontSize: 10,
        fontWeight: 'bold',
        backgroundColor: '#EAB308',
        color: 'white',
        padding: 8,
        textAlign: 'center',
      },
      centerContainer: {
        width: '50%',
        alignItems: 'center',
      },
      orgName: {
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 5,
      },
      eventSummary: {
        fontSize: 12,
        textAlign: 'center',
        marginBottom: 3,
      },
      dateRange: {
        fontSize: 10,
        textAlign: 'center',
        color: '#666',
      },
      rightContainer: {
        width: '25%',
        alignItems: 'flex-end',
        justifyContent: 'center',
      },
      printedOn: {
        fontSize: 8,
        color: '#666',
        textAlign: 'right',
      },
      timestamp: {
        fontSize: 8,
        color: '#666',
        textAlign: 'right',
        marginTop: 2,
      },
      table: {
        display: 'table',
        width: '100%',
        marginTop: 20,
      },
      tableRow: {
        flexDirection: 'row',
      },
      headerRowTable: {
        flexDirection: 'row',
        backgroundColor: '#f3f4f6',
      },
      headerText: {
        fontSize: 7,
        fontWeight: 'bold',
      },
      cellText: {
        fontSize: 7,
      },
    })

    const format = (section) => {
      const { sectionNumber, ticketsSold, ticketProceeds, scanned, unScanned } =
        section

      return {
        section_name: sectionNumber,
        tickets_sold: formatNumber(ticketsSold),
        ticket_proceeds: formatCurrency(ticketProceeds),
        scanned: formatNumber(scanned),
        unscanned: formatNumber(unScanned),
        unused: formatPercent(ticketsSold, unScanned),
      }
    }

    const schema = Object.values(reportData).map((section) => format(section))

    // Create column styles dynamically from schema
    const columnStyles = {}
    // schema.columns.forEach((col, index) => {
    //   columnStyles[`col${index}`] = {
    //     width: col.width,
    //     borderStyle: "solid",
    //     borderWidth: 0.5,
    //     padding: 3,
    //   };
    // });

    const dynamicStyles = StyleSheet.create(columnStyles)

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <View style={styles.headerRow}>
            <View style={styles.logoContainer}>
              <Text style={styles.logoText}>LOGO</Text>
            </View>
            <View style={styles.centerContainer}>
              <Text style={styles.orgName}>{organizationName}</Text>
              {/* <Text style={styles.eventSummary}>{schema.title}</Text> */}
            </View>
            <View style={styles.rightContainer}>
              <Text style={styles.printedOn}>Printed On:</Text>
              <Text style={styles.timestamp}>
                {new Date().toLocaleString()}
              </Text>
            </View>
          </View>

          <View style={styles.table}>
            <View style={[styles.tableRow, styles.headerRowTable]}>
              {Object.keys(schema).map((col, index) => (
                <View key={index}>
                  <Text style={styles.headerText}>{formatString(col)}</Text>
                </View>
              ))}
            </View>

            {/* {reportData.map((item, rowIndex) => (
              <View key={rowIndex} style={styles.tableRow}>
                {schema.columns.map((col, colIndex) => (
                  <View key={col.key} style={dynamicStyles[`col${colIndex}`]}>
                    <Text style={styles.cellText}>
                    </Text>
                  </View>
                ))}
              </View>
            ))} */}
          </View>
        </Page>
      </Document>
    )
  }

  // Universal Excel Generator using JSON schema
  const handleExcelExport = async (
    organizationName,
    event,
    reportData,
    totals
  ) => {
    const format = (obj) => {
      const {
        sectionNumber,
        source,
        ticketProceeds,
        ticketsSold,
        scanned,
        unScanned,
        parentSource,
        subGroupParent,
        title,
        name,
      } = obj

      return {
        ...(sectionNumber && { section: sectionNumber }),
        ...(source && {
          ticket_type: capitalizeString(title) || capitalizeString(name),
        }),
        tickets_sold: ticketsSold,
        ticket_proceeds: ticketProceeds,
        scanned,
        unscanned: unScanned,
        unused: unScanned / ticketsSold || 0,
        parent_source: parentSource ?? undefined,
        sub_group_parent: subGroupParent ?? false,
      }
    }

    const hideColumn = (worksheet) => {
      worksheet.getColumn('parent_source').hidden = true
      worksheet.getColumn('sub_group_parent').hidden = true
    }

    const getUnusedTotal = (totals) => {
      return totals.unScanned / totals.ticketsSold
    }

    const getColNum = (type) => {
      const soldTotalColumn = 'B'
      const ticketProceedsTotalColumn = 'C'
      const scannedTotalColumn = 'D'
      const unscannedTotalColumn = 'E'
      const unusedTotalColumn = 'F'

      switch (type) {
        case 'sold':
          return soldTotalColumn
        case 'proceeds':
          return ticketProceedsTotalColumn
        case 'scanned':
          return scannedTotalColumn
        case 'unscanned':
          return unscannedTotalColumn
        case 'unused':
          return unusedTotalColumn

        default:
          return
      }
    }

    const getBySectionData = (reportData, formatter, headerRows, worksheet) => {
      const summaryBySectionData = reportData?.attendanceBySection

      const sections = Object.keys(summaryBySectionData)

      setColumnsAndRows(
        Object.keys(formatter(summaryBySectionData[sections[0]])),
        Object.values(summaryBySectionData),
        formatter,
        worksheet
      )

      hideColumn(worksheet)

      const lastRowNum = worksheet.lastRow.number
      const totalRowNum = lastRowNum + 1

      // total row
      worksheet.getCell(`A${totalRowNum}`).value = 'TOTAL'
      worksheet.getCell(`${getColNum('sold')}${totalRowNum}`).value =
        totals.ticketsSold
      worksheet.getCell(`${getColNum('proceeds')}${totalRowNum}`).value =
        totals.ticketProceeds
      worksheet.getCell(`${getColNum('scanned')}${totalRowNum}`).value =
        totals.scanned
      worksheet.getCell(`${getColNum('unscanned')}${totalRowNum}`).value =
        totals.unScanned
      worksheet.getCell(`${getColNum('unused')}${totalRowNum}`).value =
        getUnusedTotal(totals)

      const lastRow = worksheet.lastRow

      styleTotalRow(lastRow, worksheet1, true)

      numberFormat(2, worksheet, headerRows)
      currencyFormat(3, worksheet, headerRows)
      numberFormat(4, worksheet, headerRows)
      numberFormat(5, worksheet, headerRows)
      percentFormat(6, worksheet, headerRows)
    }

    const getByTicketTypeData = (
      reportData,
      formatter,
      headerRows,
      worksheet
    ) => {
      const types = Object.keys(reportData)

      const assignmentGroups = ['comp', 'individual', 'group']

      const flattenGroups = (data, parentSource = null) => {
        // Convert the object to an array of all its values (Box Office, Website, etc.)
        return Object.entries(data).flatMap(([key, group], idx) => {
          // skips the very first parent object (attendanceBySection)
          if (idx === 0 && !parentSource) return
          // Extract the subGroups property from the current group
          // Everything else is stored in flatGroup (e.g. source, title, ticketsSold, etc.)
          const { subGroups, ...flatGroup } = group
          // Attach the parent source (if this is a nested subgroup)
          flatGroup.parentSource = parentSource
          flatGroup.name = key
          // if sources match subGroup = means that it is a nested subgroup
          flatGroup.subGroupParent = assignmentGroups?.includes(
            flatGroup?.source
          )
          // If there are subGroups inside this group, recursively flatten them
          // and pass the current group's source as their parentSource
          const children = subGroups
            ? flattenGroups(subGroups, group.source)
            : []
          // Return an array that includes:
          // - the current flattened group (flatGroup)
          // - all of its recursively flattened subgroups (children)
          return [flatGroup, ...children]
        })
      }
      // Flatten everything
      const flattened = flattenGroups(reportData).filter(Boolean)

      setColumnsAndRows(
        Object.keys(formatter(reportData[types[1]])),
        flattened,
        formatter,
        worksheet
      )

      hideColumn(worksheet)

      const getParentSourceValue = (row) => row.getCell('parent_source').value

      const getSubGroupParentSourceValue = (row) =>
        row.getCell('sub_group_parent').value

      // style total rows
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= headerRows) return // Skip title and header rows
        const isSubItem = getParentSourceValue(row)
        const isSubGroupParent = getSubGroupParentSourceValue(row)

        // parent group
        if (!isSubItem) {
          styleTotalRow(row, worksheet, true)
          // sub group
        } else if (isSubGroupParent) {
          styleSubTotalRow(row, worksheet, true)
        }
      })

      // total row
      const lastRowNum = worksheet.lastRow.number
      const totalRow = worksheet.getRow(lastRowNum + 1)
      const totalRowNumber = worksheet.getRow(lastRowNum + 1).number

      worksheet.getCell(`A${totalRowNumber}`).value = 'TOTAL'
      worksheet.getCell(`${getColNum('sold')}${totalRowNumber}`).value =
        totals.ticketsSold
      worksheet.getCell(`${getColNum('proceeds')}${totalRowNumber}`).value =
        totals.ticketProceeds
      worksheet.getCell(`${getColNum('scanned')}${totalRowNumber}`).value =
        totals.scanned
      worksheet.getCell(`${getColNum('unscanned')}${totalRowNumber}`).value =
        totals.unScanned
      worksheet.getCell(`${getColNum('unused')}${totalRowNumber}`).value =
        getUnusedTotal(totals)

      styleTotalRow(totalRow, worksheet, true)

      numberFormat(2, worksheet, headerRows)
      currencyFormat(3, worksheet, headerRows)
      numberFormat(4, worksheet, headerRows)
      numberFormat(5, worksheet, headerRows)
      percentFormat(6, worksheet, headerRows)
    }

    const workbook = createWorkbook()
    const worksheet1 = addWorksheet('Summary by Section', workbook)
    const worksheet2 = addWorksheet('Summary by Ticket Type', workbook)
    const headers = [
      `Event: ${event?.name} - ${formatShortDate(getTimezoneDate(event?.start, timezones(event?.venue?.timezone)), 'dateTime')}`,
    ]

    const headerRows = DEFAULTNUMHEADERS + headers?.length

    setHeaders('Summary by Section', organizationName, worksheet1, headers)
    setHeaders('Summary by Ticket Type', organizationName, worksheet2, headers)
    getBySectionData(reportData, format, headerRows, worksheet1)
    getByTicketTypeData(reportData, format, headerRows, worksheet2)

    // Generate and download Excel file
    // save excel worksheet
    saveWorkbook(workbook, 'access control attendance summary')
      .then()
      .catch((err) => alert(err.message))
  }

  const generatePDFFromSchema = async (reportData, organizationName) => {
    const doc = (
      <UniversalPDFReport
        reportData={reportData}
        organizationName={organizationName}
      />
    )

    const blob = await pdf(doc).toBlob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `attendance-summary.pdf`
    link.click()
    URL.revokeObjectURL(url)
  }

  function calculateTotals() {
    const totals = {
      ticketsSold: 0,
      ticketProceeds: 0,
      scanned: 0,
      unScanned: 0,
    }

    Object.values(eventAnalytics || {}).forEach((source) => {
      totals.ticketsSold += source.ticketsSold || 0
      totals.ticketProceeds += source.ticketProceeds || 0
      totals.scanned += source.scanned || 0
      totals.unScanned += source.unScanned || 0
    })

    return totals
  }

  function sortSectionNumber(a, b) {
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  }

  // Sort object by keys with custom comparator
  function sortObjectByKeys(obj, comparator) {
    const sorted = {}
    const keys = Object.keys(obj).sort(comparator)

    for (const key of keys) {
      sorted[key] = obj[key]
    }

    return sorted
  }

  // Sort subGroups alphabetically by source
  function sortSubGroups(subGroups) {
    return sortObjectByKeys(subGroups, (a, b) => {
      const sourceA = subGroups[a].source || a
      const sourceB = subGroups[b].source || b
      return sourceA.localeCompare(sourceB)
    })
  }

  // Sort subGroups with Standard admission first, then Complimentary, then alphabetical
  function sortSubGroupsWithComplimentaryAndStandard(subGroups) {
    return sortObjectByKeys(subGroups, (a, b) => {
      const sourceA = subGroups[a].source || a
      const sourceB = subGroups[b].source || b

      const isStandardA = sourceA.toLowerCase().includes('standard admission')
      const isStandardB = sourceB.toLowerCase().includes('standard admission')
      const isComplimentaryA = sourceA.toLowerCase().includes('complimentary')
      const isComplimentaryB = sourceB.toLowerCase().includes('complimentary')

      // Standard admission always first
      if (isStandardA && !isStandardB) return -1
      if (!isStandardA && isStandardB) return 1

      // Complimentary second (after standard admission)
      if (isComplimentaryA && !isComplimentaryB) return -1
      if (!isComplimentaryA && isComplimentaryB) return 1

      // Otherwise sort alphabetically
      return sourceA.localeCompare(sourceB)
    })
  }

  // Sort subGroups with "Standard" items first, then alphabetically
  function sortSubGroupsWithStandard(subGroups) {
    return sortObjectByKeys(subGroups, (a, b) => {
      const sourceA = subGroups[a].source || a
      const sourceB = subGroups[b].source || b

      const hasStandardA =
        sourceA.includes('(Standard)') || sourceA.includes('= Standard')
      const hasStandardB =
        sourceB.includes('(Standard)') || sourceB.includes('= Standard')

      // If one has Standard and the other doesn't, Standard comes first
      if (hasStandardA && !hasStandardB) return -1
      if (!hasStandardA && hasStandardB) return 1

      // Otherwise sort alphabetically
      return sourceA.localeCompare(sourceB)
    })
  }

  // Main sorting function
  function sortEventData(data) {
    if (!data) return data

    // Define the main section order
    const sectionOrder = [
      'website',
      'ticket_assignment',
      'box_office',
      'packages',
      'flex_packs',
    ]
    const sorted = {}

    // Sort attendanceBySection by sectionNumber
    if (data.attendanceBySection) {
      sorted.attendanceBySection = sortObjectByKeys(
        data.attendanceBySection,
        (a, b) =>
          sortSectionNumber(
            data.attendanceBySection[a].sectionNumber,
            data.attendanceBySection[b].sectionNumber
          )
      )
    }

    // Add sections in the specified order
    for (const sectionKey of sectionOrder) {
      if (data[sectionKey]) {
        sorted[sectionKey] = { ...data[sectionKey] }

        // Handle website: Standard admission first, then Complimentary, then alphabetical
        if (sectionKey === 'website' && sorted[sectionKey].subGroups) {
          sorted[sectionKey].subGroups =
            sortSubGroupsWithComplimentaryAndStandard(
              sorted[sectionKey].subGroups
            )
        }

        // Handle ticket_assignment: comp, individual, group (each with Standard first)
        if (
          sectionKey === 'ticket_assignment' &&
          sorted[sectionKey].subGroups
        ) {
          const ticketOrder = ['comp', 'individual', 'group']
          const customSorted = {}

          for (const key of ticketOrder) {
            if (sorted[sectionKey].subGroups[key]) {
              const item = { ...sorted[sectionKey].subGroups[key] }
              // Sort the subGroups within each category with Standard first
              if (item.subGroups) {
                item.subGroups = sortSubGroupsWithStandard(item.subGroups)
              }
              customSorted[key] = item
            }
          }

          // Add any remaining keys not in the order
          for (const key of Object.keys(sorted[sectionKey].subGroups)) {
            if (!ticketOrder.includes(key)) {
              customSorted[key] = sorted[sectionKey].subGroups[key]
            }
          }

          sorted[sectionKey].subGroups = customSorted
        }

        // Handle box_office: Standard admission first, then Complimentary, then alphabetical
        if (sectionKey === 'box_office' && sorted[sectionKey].subGroups) {
          sorted[sectionKey].subGroups =
            sortSubGroupsWithComplimentaryAndStandard(
              sorted[sectionKey].subGroups
            )
        }

        // Handle packages: alphabetical
        if (sectionKey === 'packages' && sorted[sectionKey].subGroups) {
          sorted[sectionKey].subGroups = sortSubGroups(
            sorted[sectionKey].subGroups
          )
        }

        // Handle flex_packs: alphabetical
        if (sectionKey === 'flex_packs' && sorted[sectionKey].subGroups) {
          sorted[sectionKey].subGroups = sortSubGroups(
            sorted[sectionKey].subGroups
          )
        }
      }
    }

    // Add any remaining sections not in the order
    for (const key of Object.keys(data)) {
      if (!sectionOrder.includes(key) && key !== 'attendanceBySection') {
        sorted[key] = data[key]
      }
    }

    return sorted
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <section className="spacer-sm" id="analytics-wrapper">
          <header className="section-header-sm report-page-header">
            <div className="section-heading">
              <h1>Club Seat Summary</h1>
            </div>
            <p className="section-header-desc">View proceeds for club seats.</p>
          </header>
          {seasons?.length > 0 ? (
            <>
              <ReportActions
                isLoading={isLoadingData}
                onRefresh={refresh}
                isRefreshing={isLoadingData}
                selected={selectedSeasonId}
                data={eventAnalytics}
                downloadReport={() =>
                  handleExcelExport(
                    organization?.name,
                    seasons?.find(
                      (season) => season?.uuid === selectedSeasonId
                    ),
                    eventAnalytics,
                    calculateTotals()
                  )
                }
              >
                <SeasonSelector
                  setSelected={setSelectedSeasonId}
                  selected={selectedSeasonId}
                  seasons={seasons}
                  isLoading={isLoadingData}
                />
              </ReportActions>
              <Results
                isLoading={isLoadingData}
                selectedEvent={selectedSeasonId}
                data={eventAnalytics}
                calculateTotals={calculateTotals}
              />
            </>
          ) : (
            <Card body>
              <EmptyContainer style="center lg report">
                <p>No events created</p>
                <p className="subtitle subtitle--dark">
                  Create an event to start tracking its attendance.
                </p>
              </EmptyContainer>
            </Card>
          )}
        </section>
      )}
    </>
  )
}

const formatPercent = (sold, unscanned) => {
  if (sold > 0) {
    const percentage = ((unscanned / sold) * 100).toFixed(2)
    return `${percentage}%`
  }
  return '0%'
}

// Shared row component
const ItemRow = ({ title, data, indentSize }) => (
  <tr className={`md-x ${indentSize ? `md-x-${indentSize}` : ''} `}>
    <td colSpan={3}>{capitalizeString(title)}</td>
    <td className="whitespace-nowrap">{formatNumber(data.ticketsSold)}</td>
    <td className="whitespace-nowrap">{formatCurrency(data.ticketProceeds)}</td>
    <td className="whitespace-nowrap">{formatNumber(data.scanned)}</td>
    <td className="whitespace-nowrap">{formatNumber(data.unScanned)}</td>
    <td className="whitespace-nowrap">
      {formatPercent(data.ticketsSold, data.unScanned)}
    </td>
  </tr>
)

// Shared table headers
const TableHeader = ({ first }) => (
  <thead>
    <tr className="md">
      <th className="uppercase text-body" colSpan={2}>
        Customer Name
      </th>
      <th className="uppercase text-body">Club Seat Location</th>
      <th className="uppercase text-body">Date of Purchase</th>
      <th className="uppercase text-body md">License Fees</th>
    </tr>
  </thead>
)

function Results({ isLoading, selectedEvent, data, calculateTotals }) {
  const [key, setKey] = useState('sections')

  const handleKey = (k) => {
    setKey(k)
  }

  const totals = calculateTotals()

  if (!selectedEvent) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select an event, season or date range from the options above. Once
            you've made your selections, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There's currently no data for this event. Please choose another
            event.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return <ClubSeatsTable eventAnalytics={data} totals={totals} />
}

function ClubSeatsTable({ eventAnalytics }) {
  if (!eventAnalytics) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No ticket type data available</p>
          <p className="subtitle subtitle--dark">
            Type-based analytics will be displayed here once available.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <div className="table-container">
      <div className="table-container--fixed-height--scrollable">
        <Table responsive>
          <TableHeader />
          <tbody>
            {eventAnalytics &&
              eventAnalytics?.map((data, index) => (
                <Fragment key={index}>
                  <tr>
                    <td colSpan={2}>
                      {data.firstName} {data.lastName}
                    </td>
                    <td>
                      {formatString(data.section)} • {data.row} • {data.seats}
                    </td>
                    <td>{data.orderDate}</td>
                    <td>{formatCurrency(data.type === 'package' ? 300 : 0)}</td>
                  </tr>
                </Fragment>
              ))}
          </tbody>
        </Table>
      </div>
    </div>
  )
}
